/**
 * SAN DIEGO HUMOR — Theme JavaScript
 * "Sarcastic Coastal" interactive layer
 *
 * Features:
 *  - Sticky header with transparent->solid transition
 *  - Mobile menu drawer
 *  - Burrito cursor follower (desktop)
 *  - Quick-add drawer (slides from bottom)
 *  - Neighborhood product filters
 *  - Guac add-to-cart animation
 *  - AJAX cart with drawer refresh
 */

(function () {
  'use strict';

  /* ----------------------------------------
     HEADER — Transparent-to-solid on scroll
     ---------------------------------------- */
  const StickyHeader = {
    init() {
      this.header = document.querySelector('[data-header]');
      if (!this.header) return;

      this.isTransparent = this.header.dataset.transparent === 'true';
      this.scrollThreshold = 50;

      this._onScroll = this._handleScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._handleScroll();
    },

    _handleScroll() {
      const scrolled = window.scrollY > this.scrollThreshold;
      this.header.classList.toggle('is-scrolled', scrolled);

      if (this.isTransparent) {
        this.header.classList.toggle('site-header--transparent', !scrolled);
      }
    },
  };

  /* ----------------------------------------
     MOBILE MENU DRAWER
     ---------------------------------------- */
  const MobileMenu = {
    init() {
      this.drawer = document.getElementById('MobileMenuDrawer');
      if (!this.drawer) return;

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-mobile-menu-toggle]')) {
          e.preventDefault();
          this._toggle();
        }
        if (e.target.closest('[data-mobile-menu-close]')) {
          this._close();
        }
        const accordionBtn = e.target.closest('[data-mobile-accordion-toggle]');
        if (accordionBtn) {
          e.preventDefault();
          const expanded = accordionBtn.getAttribute('aria-expanded') === 'true';
          const subnav = accordionBtn.nextElementSibling;
          accordionBtn.setAttribute('aria-expanded', String(!expanded));
          subnav.setAttribute('aria-hidden', String(expanded));
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this._close();
        }
      });
    },

    _toggle() {
      if (this.drawer.classList.contains('is-open')) {
        this._close();
      } else {
        this._open();
      }
    },

    _open() {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    _close() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },
  };

  /* ----------------------------------------
     BURRITO CURSOR FOLLOWER (desktop)
     ---------------------------------------- */
  const BurritoCursor = {
    init() {
      this.cursor = document.getElementById('BurritoCursor');
      if (!this.cursor) return;

      // Only on desktop with no touch
      if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
        this.cursor.style.display = 'none';
        return;
      }

      this.mouseX = 0;
      this.mouseY = 0;
      this.cursorX = 0;
      this.cursorY = 0;
      this.ease = 0.12;
      this.running = false;

      document.addEventListener('mousemove', (e) => {
        this.mouseX = e.clientX;
        this.mouseY = e.clientY;
        if (!this.running) {
          this.running = true;
          this._animate();
        }
      });
    },

    _animate() {
      const dx = this.mouseX - this.cursorX;
      const dy = this.mouseY - this.cursorY;

      this.cursorX += dx * this.ease;
      this.cursorY += dy * this.ease;

      this.cursor.style.transform =
        `translate3d(${this.cursorX - 16}px, ${this.cursorY - 16}px, 0)`;

      // Stop animating when close enough to target
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
        this.running = false;
        return;
      }

      requestAnimationFrame(() => this._animate());
    },
  };

  /* ----------------------------------------
     QUICK-ADD DRAWER
     ---------------------------------------- */
  const QuickAddDrawer = {
    init() {
      this.drawer = document.getElementById('QuickAddDrawer');
      this.content = document.getElementById('QuickAddContent');
      if (!this.drawer) return;

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-add-trigger]');
        if (trigger) {
          e.preventDefault();
          this._open(trigger.dataset.quickAddTrigger);
        }

        const close = e.target.closest('[data-quick-add-close]');
        if (close) {
          this._close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this._close();
        }
      });
    },

    async _open(productUrl) {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      try {
        const response = await fetch(productUrl + '?sections=quick-add-form');
        const data = await response.json();

        if (data['quick-add-form']) {
          this.content.innerHTML = data['quick-add-form'];
        } else {
          window.location.href = productUrl;
        }
      } catch {
        window.location.href = productUrl;
      }
    },

    _close() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      setTimeout(() => {
        this.content.innerHTML = '';
      }, 300);
    },
  };

  /* ----------------------------------------
     CART DRAWER
     ---------------------------------------- */
  const CartDrawer = {
    init() {
      this.drawer = document.getElementById('CartDrawer');
      if (!this.drawer) return;

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-toggle]')) {
          e.preventDefault();
          this._refresh();
          this._open();
        }
        if (e.target.closest('[data-cart-drawer-close]')) {
          this._close();
        }
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          this._removeItem(removeBtn);
        }
        const qtyBtn = e.target.closest('[data-cart-qty-change]');
        if (qtyBtn) {
          e.preventDefault();
          this._changeQty(qtyBtn);
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this._close();
        }
      });

      // Expose globally so AjaxCart can call it after add-to-cart
      window.refreshCartDrawer = () => this._refresh();
    },

    _open() {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    _close() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    async _refresh() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const contentEl = document.getElementById('CartDrawerContent');
        const footerEl = document.getElementById('CartDrawerFooter');
        const totalEl = document.getElementById('CartDrawerTotal');

        if (cart.item_count === 0) {
          contentEl.innerHTML = '<p class="cart-drawer__empty">Cart is empty. Go find something funny.</p>';
          footerEl.classList.add('is-hidden');
          // Update shipping bars to zero
          if (typeof window.updateShippingBars === 'function') {
            window.updateShippingBars(0);
          }
          return;
        }

        let html = '';
        cart.items.forEach((item) => {
          html += '<div class="cart-drawer__item">';
          if (item.image) {
            html += '<a href="' + item.url + '" class="cart-drawer__item-image-link">';
            html += '<img src="' + this._getSizedImageUrl(item.image, '100x') + '" alt="' + this._escapeHtml(item.title) + '" width="60" height="60" class="cart-drawer__item-image" loading="lazy">';
            html += '</a>';
          }
          html += '<div class="cart-drawer__item-info">';
          html += '<a href="' + item.url + '" class="cart-drawer__item-title-link">' + this._escapeHtml(item.product_title) + '</a>';
          if (item.variant_title) {
            html += '<p class="cart-drawer__item-variant">' + this._escapeHtml(item.variant_title) + '</p>';
          }
          html += '<p class="cart-drawer__item-unit-price">' + this._formatMoney(item.price) + '</p>';
          html += '<div class="cart-drawer__item-controls">';
          html += '<div class="quantity-selector quantity-selector--sm">';
          html += '<button type="button" class="quantity-selector__btn" data-cart-qty-change data-line-key="' + item.key + '" data-delta="-1" aria-label="Decrease quantity">\u2212</button>';
          html += '<span class="quantity-selector__value">' + item.quantity + '</span>';
          html += '<button type="button" class="quantity-selector__btn" data-cart-qty-change data-line-key="' + item.key + '" data-delta="1" aria-label="Increase quantity">+</button>';
          html += '</div>';
          html += '<button class="cart-drawer__item-remove" data-cart-remove data-line-key="' + item.key + '" data-line-qty="0" aria-label="Remove ' + this._escapeHtml(item.product_title) + '">';
          html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path></svg>';
          html += '</button>';
          html += '</div>';
          html += '</div>';
          html += '</div>';
        });

        contentEl.innerHTML = html;
        totalEl.textContent = this._formatMoney(cart.total_price);
        footerEl.classList.remove('is-hidden');

        // Update shipping bars
        if (typeof window.updateShippingBars === 'function') {
          window.updateShippingBars(cart.total_price);
        }
      } catch {
        // Silently fail
      }
    },

    async _updateCart(key, newQty, triggerEl) {
      if (triggerEl) {
        triggerEl.disabled = true;
        triggerEl.style.opacity = '0.3';
      }
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: Math.max(0, newQty) })
        });
        const cart = await response.json();

        /* Update header cart count */
        const headerCount = document.querySelector('.site-header__cart-count');
        if (headerCount) headerCount.textContent = cart.item_count;

        /* Update shipping bars */
        if (typeof window.updateShippingBars === 'function') {
          window.updateShippingBars(cart.total_price);
        }

        /* Re-render the drawer */
        this._refresh();
      } catch {
        if (triggerEl) {
          triggerEl.disabled = false;
          triggerEl.style.opacity = '';
        }
      }
    },

    async _removeItem(btn) {
      this._updateCart(btn.dataset.lineKey, 0, btn);
    },

    async _changeQty(btn) {
      const key = btn.dataset.lineKey;
      const delta = parseInt(btn.dataset.delta);
      /* Find current qty from sibling span */
      const selector = btn.closest('.quantity-selector');
      const currentQty = parseInt(selector.querySelector('.quantity-selector__value').textContent) || 1;
      const newQty = currentQty + delta;
      if (newQty < 1) return;
      this._updateCart(key, newQty, btn);
    },

    _formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    },

    _escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },

    _getSizedImageUrl(url, size) {
      if (!url) return '';
      const match = url.match(/(.+)\.(jpg|jpeg|png|gif|webp)(\?.*)?/i);
      if (match) {
        return match[1] + '_' + size + '.' + match[2] + (match[3] || '');
      }
      return url;
    },
  };

  /* ----------------------------------------
     AJAX ADD-TO-CART
     ---------------------------------------- */
  const AjaxCart = {
    init() {
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('.product-card__form');
        if (!form) return;

        e.preventDefault();
        const btn = form.querySelector('[data-add-to-cart]');
        const textEl = btn.querySelector('.product-card__add-text');
        const originalText = textEl.textContent;

        btn.disabled = true;
        textEl.textContent = 'Adding...';

        try {
          const formData = new FormData(form);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
          });

          if (response.ok) {
            textEl.textContent = 'Added!';
            this._updateCartCount();

            // Refresh cart drawer so it shows current items
            if (typeof window.refreshCartDrawer === 'function') {
              window.refreshCartDrawer();
            }

            setTimeout(() => {
              textEl.textContent = originalText;
              btn.disabled = false;
            }, 1500);
          } else {
            throw new Error('Add to cart failed');
          }
        } catch {
          textEl.textContent = 'Error';
          setTimeout(() => {
            textEl.textContent = originalText;
            btn.disabled = false;
          }, 1500);
        }
      });
    },

    async _updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const count = cart.item_count;

        // Update header cart count
        const headerCount = document.querySelector('.site-header__cart-count');
        if (headerCount) {
          headerCount.textContent = count;
        } else if (count > 0) {
          const cartBtn = document.querySelector('.site-header__cart');
          if (cartBtn) {
            const badge = document.createElement('span');
            badge.className = 'site-header__cart-count';
            badge.textContent = count;
            cartBtn.appendChild(badge);
          }
        }

        // Update mobile nav badge
        const mobileCartItem = document.querySelector('.mobile-bottom-nav__item[data-cart-count]');
        if (mobileCartItem) {
          mobileCartItem.setAttribute('data-cart-count', count);
          let mobileBadge = mobileCartItem.querySelector('.mobile-bottom-nav__badge');
          if (count > 0) {
            if (!mobileBadge) {
              mobileBadge = document.createElement('span');
              mobileBadge.className = 'mobile-bottom-nav__badge';
              mobileCartItem.appendChild(mobileBadge);
            }
            mobileBadge.textContent = count;
          } else if (mobileBadge) {
            mobileBadge.remove();
          }
        }

        // Update aria label
        const cartToggle = document.querySelector('[data-cart-toggle]');
        if (cartToggle) {
          cartToggle.setAttribute('aria-label', `Cart (${count} items)`);
        }
      } catch {
        // Silently fail
      }
    },
  };

  /* ----------------------------------------
     QUICK VIEW MODAL
     ---------------------------------------- */
  const QuickView = {
    init() {
      this.modal = document.getElementById('QuickViewModal');
      this.panel = document.getElementById('QuickViewPanel');
      this.loading = document.getElementById('QuickViewLoading');
      if (!this.modal) return;

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-view-trigger]');
        if (trigger) {
          e.preventDefault();
          const url = trigger.dataset.productUrl;
          const handle = trigger.dataset.productHandle;
          this._open(url, handle);
        }
        if (e.target.closest('[data-quick-view-close]')) {
          this._close();
        }
        // Swatch image + price swap on product cards
        const swatch = e.target.closest('[data-swatch-trigger]');
        if (swatch) {
          const card = swatch.closest('[data-product-card]');
          if (!card) return;
          const mainImg = card.querySelector('.product-card__image:not(.product-card__image--hover)');
          if (mainImg && swatch.dataset.variantImage) {
            mainImg.src = swatch.dataset.variantImage;
            mainImg.srcset = '';
          }
          const hoverImg = card.querySelector('.product-card__image--hover');
          if (hoverImg && swatch.dataset.variantImage) {
            hoverImg.src = swatch.dataset.variantImage;
            hoverImg.srcset = '';
          }
          card.querySelectorAll('.product-card__swatch').forEach(s => s.classList.remove('is-active'));
          swatch.classList.add('is-active');

          // Update price + sale badge to match this variant
          const priceCents = parseInt(swatch.dataset.variantPrice, 10);
          const compareCents = parseInt(swatch.dataset.variantCompareAtPrice, 10) || 0;
          if (!isNaN(priceCents)) {
            const fmt = (c) => '$' + (c / 100).toFixed(2);
            const priceEl = card.querySelector('[data-card-price]');
            if (priceEl) {
              const onSale = compareCents > priceCents;
              let html = '';
              if (onSale) {
                html += '<span class="product-card__price--compare">' + fmt(compareCents) + '</span>';
              }
              html += '<span class="product-card__price--current' + (onSale ? ' product-card__price--sale' : '') + '">' + fmt(priceCents) + '</span>';
              priceEl.innerHTML = html;
            }
            const badge = card.querySelector('[data-card-sale-badge]');
            if (badge) {
              if (compareCents > priceCents) {
                const discount = Math.round((compareCents - priceCents) * 100 / compareCents);
                badge.textContent = '-' + discount + '%';
                badge.style.display = '';
              } else {
                badge.textContent = '';
                badge.style.display = 'none';
              }
            }
          }
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
          this._close();
        }
      });
    },

    async _open(url, handle) {
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.loading.style.display = '';

      try {
        const resp = await fetch(url + '.js');
        const product = await resp.json();
        this._render(product, url);
      } catch (err) {
        // Fallback: navigate to product page
        window.location.href = url;
      }
    },

    _close() {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    _formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    },

    _render(product, url) {
      this.loading.style.display = 'none';

      // Build options HTML
      let optionsHtml = '';
      if (product.options && product.options.length > 0 && !(product.options.length === 1 && product.options[0].name === 'Title')) {
        product.options.forEach((option, idx) => {
          const isColor = option.name.toLowerCase().includes('color') || option.name.toLowerCase().includes('colour');
          optionsHtml += '<div class="quick-view-modal__option">';
          optionsHtml += '<span class="quick-view-modal__option-label">' + option.name + ': ' + option.values[0] + '</span>';
          optionsHtml += '<div class="quick-view-modal__option-values">';

          if (isColor) {
            // Show color swatches with images
            const seenImages = {};
            product.variants.forEach(v => {
              const colorVal = v.options[idx];
              if (!seenImages[colorVal] && v.featured_image) {
                seenImages[colorVal] = v.featured_image;
                const active = colorVal === option.values[0] ? ' is-active' : '';
                optionsHtml += '<button class="quick-view-modal__color-swatch' + active + '" data-qv-option="' + idx + '" data-qv-value="' + colorVal + '">';
                optionsHtml += '<img src="' + v.featured_image.src.replace(/\.([a-z]+)\?/, '_80x80.$1?') + '" alt="' + colorVal + '" width="32" height="32">';
                optionsHtml += '</button>';
              }
            });
          } else {
            option.values.forEach((val, valIdx) => {
              const active = valIdx === 0 ? ' is-active' : '';
              optionsHtml += '<button class="quick-view-modal__option-btn' + active + '" data-qv-option="' + idx + '" data-qv-value="' + val + '">' + val + '</button>';
            });
          }

          optionsHtml += '</div></div>';
        });
      }

      // Price
      const variant = product.variants[0];
      let priceHtml = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml = '<span class="quick-view-modal__price--compare">' + this._formatMoney(variant.compare_at_price) + '</span>';
        priceHtml += '<span class="quick-view-modal__price--sale">' + this._formatMoney(variant.price) + '</span>';
      } else {
        priceHtml = this._formatMoney(variant.price);
      }

      // Image
      const imgSrc = product.featured_image ? product.featured_image.replace(/\.([a-z]+)\?/, '_800x.$1?') : '';

      const html = `
        <div class="quick-view-modal__image">
          <img src="${imgSrc}" alt="${product.title}" loading="eager">
        </div>
        <div class="quick-view-modal__info">
          <h2 class="quick-view-modal__title">${product.title}</h2>
          <div class="quick-view-modal__price">${priceHtml}</div>
          <a href="${url}" class="quick-view-modal__view-full">View full details</a>
          ${optionsHtml}
          <div class="quick-view-modal__actions">
            <button class="quick-view-modal__add-btn" data-qv-add-to-cart data-variant-id="${variant.id}">ADD TO CART</button>
            <button class="quick-view-modal__buy-btn" data-qv-buy-now data-variant-id="${variant.id}">Buy with <strong>Shop</strong>Pay</button>
          </div>
        </div>
      `;

      // Keep close button, replace rest
      const closeBtn = this.panel.querySelector('.quick-view-modal__close');
      this.panel.innerHTML = '';
      this.panel.appendChild(closeBtn);
      this.panel.insertAdjacentHTML('beforeend', html);

      // Store product data for variant switching
      this._product = product;
      this._selectedOptions = product.options.map(o => o.values[0]);

      // Option click handlers
      this.panel.querySelectorAll('[data-qv-option]').forEach(btn => {
        btn.addEventListener('click', () => {
          const optIdx = parseInt(btn.dataset.qvOption);
          const val = btn.dataset.qvValue;
          this._selectedOptions[optIdx] = val;

          // Update active state
          btn.parentElement.querySelectorAll('[data-qv-option]').forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');

          // Update label
          const label = btn.closest('.quick-view-modal__option').querySelector('.quick-view-modal__option-label');
          if (label) {
            const optName = product.options[optIdx].name;
            label.textContent = optName + ': ' + val;
          }

          // Find matching variant
          const matched = product.variants.find(v =>
            v.options.every((o, i) => o === this._selectedOptions[i])
          );

          if (matched) {
            // Update price
            const priceEl = this.panel.querySelector('.quick-view-modal__price');
            if (matched.compare_at_price && matched.compare_at_price > matched.price) {
              priceEl.innerHTML = '<span class="quick-view-modal__price--compare">' + this._formatMoney(matched.compare_at_price) + '</span><span class="quick-view-modal__price--sale">' + this._formatMoney(matched.price) + '</span>';
            } else {
              priceEl.innerHTML = this._formatMoney(matched.price);
            }

            // Update image if variant has one
            if (matched.featured_image) {
              const img = this.panel.querySelector('.quick-view-modal__image img');
              if (img) img.src = matched.featured_image.src.replace(/\.([a-z]+)\?/, '_800x.$1?');
            }

            // Update variant ID on buttons
            this.panel.querySelectorAll('[data-variant-id]').forEach(b => {
              b.dataset.variantId = matched.id;
            });
          }
        });
      });

      // Add to cart
      this.panel.querySelector('[data-qv-add-to-cart]')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        const variantId = btn.dataset.variantId;
        btn.textContent = 'ADDING...';
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId), quantity: 1 }),
          });
          btn.textContent = 'ADDED!';
          // Update cart count in header
          AjaxCart._updateCartCount();
          setTimeout(() => {
            btn.textContent = 'ADD TO CART';
            this._close();
            // Refresh cart drawer and shipping bars
            CartDrawer._refresh?.();
          }, 1000);
        } catch {
          btn.textContent = 'ADD TO CART';
        }
      });

      // Buy now
      this.panel.querySelector('[data-qv-buy-now]')?.addEventListener('click', (e) => {
        const variantId = e.currentTarget.dataset.variantId;
        window.location.href = '/cart/' + variantId + ':1';
      });
    },
  };

  /* ----------------------------------------
     FREE SHIPPING BAR
     ---------------------------------------- */
  const FreeShippingBar = {
    threshold: 5000, // cents ($50.00)

    init() {
      this.bars = document.querySelectorAll('[data-shipping-bar]');
      if (!this.bars.length) return;

      // Read threshold from data attribute (in case it differs)
      const firstBar = this.bars[0];
      if (firstBar.dataset.threshold) {
        this.threshold = parseInt(firstBar.dataset.threshold);
      }

      // Expose globally so cart modules can call it
      window.updateShippingBars = (cartTotal) => this.update(cartTotal);
    },

    update(cartTotalCents) {
      const remaining = Math.max(0, this.threshold - cartTotalCents);
      const progress = Math.min(100, (cartTotalCents / this.threshold) * 100);

      this.bars.forEach((bar) => {
        // Update message
        const msgEl = bar.querySelector('[data-shipping-message]');
        if (msgEl) {
          if (cartTotalCents === 0) {
            msgEl.innerHTML = '<span>Spend <strong>$' + (this.threshold / 100) + '</strong> for <strong>FREE SHIPPING</strong> 🤙</span>';
          } else if (remaining > 0) {
            msgEl.innerHTML = '<span>You\'re <strong>$' + (remaining / 100).toFixed(2) + '</strong> away from <strong>FREE SHIPPING!</strong></span>';
          } else {
            msgEl.innerHTML = '<span>🤙 <strong>FREE SHIPPING</strong> unlocked! Hang ten! 🤙</span>';
          }
        }

        // Update wave width
        const waveEl = bar.querySelector('[data-shipping-wave]');
        if (waveEl) {
          waveEl.style.setProperty('--progress', progress + '%');
        }

        // Update surfer position + spin at 100%
        const surferEl = bar.querySelector('[data-shipping-surfer]');
        if (surferEl) {
          surferEl.style.setProperty('--progress', progress + '%');
          if (progress >= 100) {
            surferEl.classList.add('is-spinning');
          } else {
            surferEl.classList.remove('is-spinning');
          }
        }
      });
    },
  };

  /* ----------------------------------------
     NOTIFY-ME MODAL (collection sold-out)
     ---------------------------------------- */
  const NotifyModal = {
    init() {
      this.modal = document.getElementById('NotifyModal');
      if (!this.modal) return;

      this.form = document.getElementById('NotifyModalForm');
      this.bodyInput = document.getElementById('NotifyModalBody');
      this.productEl = document.getElementById('NotifyModalProduct');
      this.successEl = document.getElementById('NotifyModalSuccess');

      this._bindTriggers();
      this._bindClose();
      this._bindSubmit();
    },

    _bindTriggers() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-notify-trigger]');
        if (!trigger) return;
        e.preventDefault();

        const title = trigger.dataset.productTitle;
        const url = trigger.dataset.productUrl;
        this.productEl.textContent = title;
        this.bodyInput.value = 'Notify request for ' + title + ' \u2014 ' + url;

        this.form.style.display = '';
        this.successEl.style.display = 'none';
        this.form.reset();
        this.bodyInput.value = 'Notify request for ' + title + ' \u2014 ' + url;

        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      });
    },

    _close() {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    _bindClose() {
      this.modal.querySelectorAll('[data-notify-close]').forEach((el) => {
        el.addEventListener('click', () => this._close());
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
          this._close();
        }
      });
    },

    _bindSubmit() {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const data = new FormData(this.form);
        fetch('/contact', { method: 'POST', body: data })
          .then(() => {
            this.form.style.display = 'none';
            this.successEl.style.display = '';
          })
          .catch(() => {
            alert('Something went wrong. Please try again.');
          });
      });
    },
  };

  /* ----------------------------------------
     INIT ON DOM READY
     ---------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    StickyHeader.init();
    MobileMenu.init();
    BurritoCursor.init();
    CartDrawer.init();
    QuickAddDrawer.init();
    AjaxCart.init();
    QuickView.init();
    FreeShippingBar.init();
    NotifyModal.init();
  });
})();
